import { useEffect, useState, useMemo } from 'react';

const EMPTY = {
  idEmpleado: '',
  numeroCertificacion: '',
  descripcionCertificacion: '',
  institucionQueCertifica: '',
  avatarUrl: 'http://',
  bannerUrl: 'http://',
};

export default function Modal({
  open,
  mode = 'new',
  data = null,
  empleados = [],
  personas = [],
  instructores = [],
  onClose,
  onSubmit,
  inline = false,
}) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({
    idEmpleado: '',
    numeroCertificacion: '',
    descripcionCertificacion: '',
    institucionQueCertifica: '',
    avatarUrl: '',
    bannerUrl: '',
  });

  const availableEmpleados = useMemo(() => {
    const usedIds = new Set(instructores.map(i => i.idEmpleado));
    if (mode === 'edit' && data) {
      usedIds.delete(data.idEmpleado);
    }

    return empleados
      .filter(e => !usedIds.has(e.idEmpleado))
      .map(e => {
        const p = personas.find(p => p.idPersona === e.idPersona);
        const nombre = p ? `${p.nombre} ${p.apellidoPaterno} ${p.apellidoMaterno || ''}`.trim() : `Empleado ${e.idEmpleado}`;
        return { idEmpleado: e.idEmpleado, nombre };
      })
      .sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [empleados, personas, instructores, mode, data]);

  const validateField = (field, value) => {
    let error = '';
    // Skip validation for URL fields that have default values
    if (field === 'avatarUrl' || field === 'bannerUrl') {
      setErrors(prev => ({ ...prev, [field]: error }));
      return true;
    }
    if (!value) {
      error = 'Este campo es obligatorio.';
    }
    setErrors(prev => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateAll = () => {
    const newErrors = {};
    let isValid = true;
    Object.keys(EMPTY).forEach(key => {
      // Skip URL fields from validation
      if (key === 'avatarUrl' || key === 'bannerUrl') return;

      if (!form[key]) {
        newErrors[key] = 'Este campo es obligatorio.';
        isValid = false;
      }
    });
    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  const handleSubmit = () => {
    const isValid = validateAll();
    if (!isValid) return;
    onSubmit(form, mode);
  };

  useEffect(() => {
    if (mode === 'edit' && data) {
      const filledForm = {
        idInstructor: data.idInstructor ?? 0,
        idEmpleado: data.idEmpleado ?? '',
        numeroCertificacion: data.numeroCertificacion ?? '',
        descripcionCertificacion: data.descripcionCertificacion ?? '',
        institucionQueCertifica: data.institucionQueCertifica ?? '',
        avatarUrl: data.avatarUrl || 'http://',
        bannerUrl: data.bannerUrl || 'http://',
      };
      setForm(filledForm);
      // Reset errors for edit mode
      setErrors({
        idEmpleado: '',
        numeroCertificacion: '',
        descripcionCertificacion: '',
        institucionQueCertifica: '',
        avatarUrl: '',
        bannerUrl: '',
      });
    } else {
      setForm(EMPTY);
      setErrors({
        idEmpleado: '',
        numeroCertificacion: '',
        descripcionCertificacion: '',
        institucionQueCertifica: '',
        avatarUrl: '',
        bannerUrl: '',
      });
    }
  }, [mode, data]);

  if (!open) return null;

  const allRequiredFilled =
    form.idEmpleado &&
    form.numeroCertificacion &&
    form.descripcionCertificacion &&
    form.institucionQueCertifica;

  const hasErrors = Object.values(errors).some((e) => !!e);
  const isSaveDisabled = !allRequiredFilled || hasErrors;

  const errorStyle = {
    color: '#b91c1c',
    fontSize: '.8rem',
    display: 'block',
    marginTop: '4px',
  };

  const Header = (
    <div
      className="roles-topbar"
      style={{
        marginBottom: '0.6rem',
        paddingRight: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flex: '0 0 auto',
      }}>
      <h3 className="roles-title" style={{ marginRight: 0 }}>
        {mode === 'new' ? 'Nuevo instructor' : 'Editar instructor'}
      </h3>
      <button className="roles-btn" onClick={onClose} aria-label="Cerrar">✕</button>
    </div>
  );

  const Body = (
    <div
      style={{
        paddingLeft: '12%',
        paddingRight: 16,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0.8rem',
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0,
      }}>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Empleado: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <select
          className={`infoField${errors.idEmpleado ? ' infoField--error' : ''}`}
          value={form.idEmpleado}
          onChange={(e) => handleChange('idEmpleado', Number(e.target.value) || '')}
          disabled={mode === 'edit'} // Usually you don't change the employee of an instructor record
        >
          <option value="">-- Selecciona un empleado --</option>
          {availableEmpleados.map(e => (
            <option key={e.idEmpleado} value={e.idEmpleado}>{e.nombre}</option>
          ))}
        </select>
        {errors.idEmpleado && <small style={errorStyle}>{errors.idEmpleado}</small>}
      </div>

      <div>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>No. Certificación: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <input
          className={`infoField${errors.numeroCertificacion ? ' infoField--error' : ''}`}
          value={form.numeroCertificacion}
          onChange={(e) => handleChange('numeroCertificacion', e.target.value)}
          maxLength={120}
        />
        {errors.numeroCertificacion && <small style={errorStyle}>{errors.numeroCertificacion}</small>}
      </div>

      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Descripción de la certificación: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <input
          className={`infoField${errors.descripcionCertificacion ? ' infoField--error' : ''}`}
          value={form.descripcionCertificacion}
          onChange={(e) => handleChange('descripcionCertificacion', e.target.value)}
          maxLength={500}
        />
        {errors.descripcionCertificacion && <small style={errorStyle}>{errors.descripcionCertificacion}</small>}
      </div>

      <div style={{ gridColumn: '1 / 3' }}>
        <label className="roles-title" style={{ fontSize: '.95rem' }}>Institución que certifica: <span style={{ color: 'red' }}>*</span></label>
        <br /><br />
        <input
          className={`infoField${errors.institucionQueCertifica ? ' infoField--error' : ''}`}
          value={form.institucionQueCertifica}
          onChange={(e) => handleChange('institucionQueCertifica', e.target.value)}
          maxLength={200}
        />
        {errors.institucionQueCertifica && <small style={errorStyle}>{errors.institucionQueCertifica}</small>}
      </div>
    </div>
  );

  const Footer = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '.6rem',
        marginTop: '.8rem',
        paddingRight: 16,
        flex: '0 0 auto',
      }}
    >
      <button className="roles-btn" onClick={onClose}>Cancelar</button>
      <button
        className="btn-action"
        onClick={handleSubmit}
        disabled={isSaveDisabled}
        style={{
          opacity: isSaveDisabled ? 0.5 : 1,
          cursor: isSaveDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        {mode === 'new' ? 'Guardar' : 'Actualizar'}
      </button>
    </div>
  );

  if (inline) {
    return (
      <div className="roles-inline-card" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {Header}
        {Body}
        {Footer}
      </div>
    );
  }

  return (
    <div
      className="roles-confirm-overlay"
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        zIndex: 1000,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="roles-confirm-card"
        style={{
          width: 'min(900px, 96vw)',
          maxHeight: '85vh',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {Header}
        {Body}
        {Footer}
      </div>
    </div>
  );
}
